/**
 * GlassMoodFace — the five mood glyphs of the daily intro.
 *
 * A real piece of frosted glass (a div with backdrop-filter) carrying a thin
 * ink line-face: two dots and one mouth whose curve runs from a firm frown
 * (Heavy) through flat (Okay) to a wide open arc (Great). No coloured body,
 * no highlight blobs — the mood colour appears only as a ring and a faint
 * tint when the orb is hovered or chosen, driven by the parent through the
 * `data-face` hook or the `active` prop.
 */
import { Box } from '@mui/material';

export type MoodFaceValue = 'very_negative' | 'negative' | 'neutral' | 'positive' | 'very_positive';

export interface GlassMoodFaceProps {
  mood: MoodFaceValue;
  /** Mood colour — only used for the selected / hovered ring. */
  color: string;
  size?: number;
  active?: boolean;
}

const EYE = 2.7;

const FACES: Record<MoodFaceValue, JSX.Element> = {
  very_negative: (
    <>
      {/* eyes drooping at the outer corners (weary, not angry), firm frown */}
      <path d="M23 32.4 l8 -2.6" />
      <path d="M49 32.4 l-8 -2.6" />
      <path d="M25.5 50 q10.5 -11 21 0" />
    </>
  ),
  negative: (
    <>
      <circle cx="27" cy="31" r={EYE} fill="currentColor" stroke="none" />
      <circle cx="45" cy="31" r={EYE} fill="currentColor" stroke="none" />
      <path d="M27.5 49 q8.5 -5.5 17 0" />
    </>
  ),
  neutral: (
    <>
      <circle cx="27" cy="31" r={EYE} fill="currentColor" stroke="none" />
      <circle cx="45" cy="31" r={EYE} fill="currentColor" stroke="none" />
      <path d="M27.5 48 h17" />
    </>
  ),
  positive: (
    <>
      <circle cx="27" cy="30.5" r={EYE} fill="currentColor" stroke="none" />
      <circle cx="45" cy="30.5" r={EYE} fill="currentColor" stroke="none" />
      <path d="M26 45.5 q10 9.5 20 0" />
    </>
  ),
  very_positive: (
    <>
      {/* happy closed eyes, wide open smile */}
      <path d="M23 31.5 q4 -5 8 0" />
      <path d="M41 31.5 q4 -5 8 0" />
      <path d="M24.5 44 q11.5 15 23 0" />
    </>
  ),
};

export default function GlassMoodFace({ mood, color, size = 76, active = false }: GlassMoodFaceProps) {
  return (
    <Box
      data-face
      style={{ ['--face-accent' as string]: color }}
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        position: 'relative',
        display: 'grid',
        placeItems: 'center',
        // Translucent surface, no backdrop-filter: the aurora behind is
        // already soft, and five blurring discs that scale on hover would
        // each re-blur their backdrop every frame.
        background: active
          ? `color-mix(in srgb, ${color} 14%, color-mix(in srgb, var(--surface) 58%, transparent))`
          : 'color-mix(in srgb, var(--surface) 58%, transparent)',
        border: '1px solid color-mix(in srgb, var(--hairline) 85%, transparent)',
        boxShadow: active
          ? `inset 0 1px 0 rgba(255,255,255,0.5), 0 0 0 2px ${color}, 0 14px 30px ${color}44`
          : 'inset 0 1px 0 rgba(255,255,255,0.5), 0 10px 26px rgba(15,23,42,0.10)',
        transition: 'box-shadow 220ms ease, background 220ms ease',
        color: 'var(--ink)',
      }}
    >
      <svg
        width={Math.round(size * 0.78)}
        height={Math.round(size * 0.78)}
        viewBox="0 0 72 72"
        aria-hidden
        focusable="false"
      >
        <g fill="none" stroke="currentColor" strokeWidth="2.7" strokeLinecap="round" strokeLinejoin="round">
          {FACES[mood]}
        </g>
      </svg>
    </Box>
  );
}
